import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { clearLocalData } from '../db/mobileDbOperations'

const AuthContext = createContext({})

const ACTIVE_PROFILE_KEY = 'tm_profile_app_active_profile_id'

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profiles, setProfiles] = useState([])
  const [userProfile, setUserProfile] = useState(null)
  const [company, setCompany] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [loading, setLoading] = useState(true)
  const [switchKey, setSwitchKey] = useState(0)
  const [isSwitching, setIsSwitching] = useState(false)
  // { companies: [names] } when every profile on this login has been archived or removed
  // from its company; the server no longer lists those profiles. Null otherwise.
  const [accessRemoved, setAccessRemoved] = useState(null)

  const fetchUserData = useCallback(async (authId, activeProfileId) => {
    if (!authId) {
      setProfiles([])
      setUserProfile(null)
      setCompany(null)
      setUserRole(null)
      return
    }

    try {
      // Determine which profile to load
      const storedProfileId = activeProfileId || localStorage.getItem(ACTIVE_PROFILE_KEY) || null

      const { data, error } = await supabase.rpc('app_get_auth_data_v2', {
        p_auth_id: authId,
        p_profile_id: storedProfileId,
      })

      if (error) throw error

      const allProfiles = data?.profiles || []
      if (!allProfiles.length) {
        setProfiles([])
        setUserProfile(null)
        setCompany(null)
        setUserRole(null)
        // No profile listed: tell "archived or removed everywhere" apart from "not set up yet"
        const { data: access, error: accessError } = await supabase.rpc('get_my_profile_access')
        if (accessError) throw accessError
        const gone = (access || []).filter(p => p.status === 'archived' || p.status === 'removed')
        const usable = (access || []).some(p => p.status === 'active' || p.status === 'setting_up')
        if (gone.length && !usable) {
          localStorage.removeItem(ACTIVE_PROFILE_KEY)
          await clearLocalData()
          setAccessRemoved({ companies: [...new Set(gone.map(p => p.company_name).filter(Boolean))] })
        } else {
          setAccessRemoved(null)
        }
        return
      }

      setAccessRemoved(null)
      setProfiles(allProfiles)

      // Determine active profile
      let activeId = storedProfileId
      if (activeId && !allProfiles.find(p => p.id === activeId)) {
        activeId = null
        localStorage.removeItem(ACTIVE_PROFILE_KEY)
      }
      if (!activeId) {
        activeId = allProfiles[0].id
      }
      localStorage.setItem(ACTIVE_PROFILE_KEY, activeId)

      const activeProfile = allProfiles.find(p => p.id === activeId)
      setUserProfile(activeProfile)
      setCompany(data?.company || null)
      setUserRole(data?.role || null)
    } catch (error) {
      console.error('[Auth] Critical error:', error)
      setProfiles([])
      setUserProfile(null)
      setCompany(null)
      setUserRole(null)
    }
  }, [])

  const switchProfile = useCallback(async (profileId) => {
    const profile = profiles.find(p => p.id === profileId)
    if (!profile || !user) return { success: false }

    setIsSwitching(true)
    localStorage.setItem(ACTIVE_PROFILE_KEY, profileId)
    await fetchUserData(user.id, profileId)
    setSwitchKey(prev => prev + 1)
    setTimeout(() => setIsSwitching(false), 100)
    return { success: true }
  }, [profiles, user, fetchUserData])

  useEffect(() => {
    let mounted = true

    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()

        if (session?.user && mounted) {
          setUser(session.user)
          setTimeout(() => {
            if (mounted) fetchUserData(session.user.id)
          }, 200)
        }
      } catch (error) {
        console.error('[Auth] Session check error:', error)
      } finally {
        if (mounted) setLoading(false)
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return

      if (event === 'SIGNED_OUT' || !session?.user) {
        setUser(null)
        setProfiles([])
        setUserProfile(null)
        setCompany(null)
        setUserRole(null)
        setAccessRemoved(null)
        setLoading(false)
        localStorage.removeItem(ACTIVE_PROFILE_KEY)
        clearLocalData()
      } else if (event === 'SIGNED_IN' && session?.user) {
        setUser(session.user)
        setTimeout(() => {
          if (mounted) {
            fetchUserData(session.user.id)
            setLoading(false)
          }
        }, 200)
      } else if (event === 'TOKEN_REFRESHED' && session?.user && mounted) {
        setUser(session.user)
      }
    })

    checkSession()

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [fetchUserData])

  const signOut = useCallback(async () => {
    localStorage.removeItem(ACTIVE_PROFILE_KEY)
    await clearLocalData()
    await supabase.auth.signOut()
  }, [])

  const refreshUserData = useCallback(
    () => user && fetchUserData(user.id),
    [user, fetchUserData]
  )

  const value = useMemo(() => ({
    user,
    profiles,
    userProfile,
    company,
    userRole,
    loading,
    switchProfile,
    switchKey,
    isSwitching,
    signOut,
    refreshUserData,
    accessRemoved,
  }), [user, profiles, userProfile, company, userRole, loading, switchKey, isSwitching, switchProfile, signOut, refreshUserData, accessRemoved])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
